import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { WizardClearButton } from "../components/WizardControls";
import {
  Wrench,
  PhoneCall,
  Target,
  Gift,
  Microphone,
  CurrencyDollar,
  ChatCircle,
  DeviceMobile,
  UsersThree,
  Sparkle,
} from "@phosphor-icons/react";
import AgentOpeningScreen from "../components/AgentOpeningScreen";
import MagicGenerateButton from "../components/MagicGenerateButton";
import WebinarSectionCarousel from "./WebinarSectionCarousel";
import WebinarVazlatScreen from "./WebinarVazlatScreen";
import LandingRegistrationVazlatScreen from "./LandingRegistrationVazlatScreen";
import LandingRegistrationResultScreen from "./LandingRegistrationResultScreen";
import LandingThankYouResultScreen from "./LandingThankYouResultScreen";
import LandingSalesVazlatScreen from "./LandingSalesVazlatScreen";
import LandingSalesResultScreen from "./LandingSalesResultScreen";
import SalesCallClosingResultScreen from "./SalesCallClosingResultScreen";
import SalesIntroCallResultScreen from "./SalesIntroCallResultScreen";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";
import { getActiveAlap } from "../lib/activeAlap";

// Értékesítés layer accent — gold/amber #ffdb5c. Label uses a slightly
// deeper amber (#d4a800) so the small text reads clearly on the dark bg.
const GOLD_ACCENT = {
  eyebrow: "#ffdb5c",
  iconBg: "rgba(255, 219, 92, 0.15)",
  icon: "#ffdb5c",
  label: "#d4a800",
  selectedBg: "rgba(255, 219, 92, 0.10)",
  selectedBorder: "rgba(255, 219, 92, 0.50)",
  selectedIconBg: "rgba(255, 219, 92, 0.22)",
};

/* ─── 01 · Webinár Varázsló ─────────────────────────────────────────── */

// Külső wrapper komponens — CSAK a Tudástár read-only viewer route-ját
// dönti el. Ha nincs saved item, a rendes WebinarAgentInner komponens
// renderel (az wizard + carousel state-el). Ez a szétválasztás azért
// szükséges, hogy a Rules of Hooks NE sérüljön: az inner komponens
// összes useState / useEffect hook-ja garantáltan minden renderen
// ugyanabban a sorrendben fut.
export function WebinarAgent() {
  const location = useLocation();
  const navigate = useNavigate();
  const savedItem = location?.state?.savedItem || null;
  const savedBlueprint = savedItem?.blueprint || null;
  const savedCarouselContent = savedBlueprint?.carousel_content || null;

  if (savedItem && savedCarouselContent) {
    // Explicit variant mapping — biztosítja, hogy egy jövőbeni új
    // webinár sub-slug (pl. `webinar-optimize`) ne kerüljön véletlenül
    // a callbook_funnel branch-be. Ha ismeretlen a slug, fallback a
    // callbook_funnel-re (mert az a base variant), de ezt kommentálni
    // érdemes minden új slug hozzáadásakor.
    const VARIANT_MAP = {
      "webinar-direct-sales": "direct_sales",
      "webinar-callbook-funnel": "callbook_funnel",
      "webinar-callback-funnel": "callbook_funnel",
      "webinar-vazlat": "callbook_funnel",
      "webinar": "callbook_funnel",
    };
    const savedVariant = VARIANT_MAP[savedItem.agent_type] || "callbook_funnel";
    return (
      <WebinarSectionCarousel
        key={`vault-${savedItem.id || savedItem.agent_type}`}
        inputs={{ webinar_cim: savedItem.name || savedBlueprint?.webinar_cim || "" }}
        vazlat={null}
        vazlatRaw=""
        initialOpening={null}
        agentVariant={savedVariant}
        savedCarouselContent={savedCarouselContent}
        readOnly={true}
        onClose={() => navigate("/asset-vault")}
        onSavedToVault={null}
      />
    );
  }

  return <WebinarAgentInner />;
}

function WebinarAgentInner() {
  const options = [
    { id: "callbook-funnel", title: "Webinárból telefonhívás", label: "Struktúra, ami konzultációra vagy stratégiai hívásra vezet", icon: PhoneCall },
    { id: "direct",          title: "Webinárból direkt értékesítés",   label: "Struktúra, ami azonnali vásárlási döntésre ösztönöz", icon: Target },
    // "Hamarosan" — a meglévő prezentáció felturbózása. Locked kártya:
    // az AgentOpeningScreen már natívan renderel egy "HAMAROSAN" pill-t
    // + cursor:not-allowed + onSelectOption early return, ha `locked`.
    { id: "optimize",        title: "Webinár optimalizálás",  label: "Meglévő prezentációból erősebb, tisztább webinár.", icon: Wrench, locked: true },
  ];

  // Sub-agent state: which option is currently selected + the 4-field
  // intake state for the "callbook-funnel" sub-agent. The 4 input fields
  // are mode-specific — the registry-level Avatar / Márkahang / Ajánlat
  // esszencia kerül automatikus injektálásra a Writer prompt szintjén,
  // így ezeket itt nem kérdezzük újra.
  const [selected, setSelected] = useState(null);

  const showCallbookFunnelSetup = selected === "callbook-funnel" || selected === "direct";
  const agentVariant = selected === "direct" ? "direct_sales" : "callbook_funnel";

  // ── Per-variant state buckets (state izoláció) ─────────────────────
  // A "Webinárból telefonhívás" (callbook_funnel) és a "Közvetlen
  // értékesítés" (direct_sales) sub-agent teljesen IZOLÁLT állapottal
  // rendelkezik: input mezők, phase, vázlat, jobId, generált 1.
  // szekció eredmény — mindent variant-kulcsú bucket-ekben tartunk,
  // így a user szabadon váltogathat a két sub-agent között anélkül,
  // hogy az egyik munkája felülírná a másikét. A `key={agentVariant}`
  // trigger a gyerek képernyőkön garantálja, hogy a downstream React
  // state (Carousel sections, refine, undoSnapshot, VazlatScreen
  // suggestion state, stb.) is friss variant-váltásnál.
  //
  // phase: "opening" → user 4 mezős intake-en van (opening cards)
  //         "vazlat"  → Vázlatképernyő (5 kártya + finomító panel)
  //         "carousel"→ Progresszív szekciónkénti webinár (v7 writer)
  const emptyBucket = () => ({
    wcim: "", wtanulas: "", wmiertte: "", wcta: "",
    phase: "opening",
    vazlatLoading: false, vazlatBtnLabel: "", vazlatError: "",
    vazlat: null, vazlatRaw: "", vazlatJobId: null,
    openingLoading: false, openingLabel: "", openingResult: null,
  });
  // Csak a strukturális mezőket mentsük drafként (loading flag-eket,
  // hibaszövegeket, job_id-t nem — refresh után úgyis irrelevánsak).
  const pickDraft = (b) => {
    if (!b) return null;
    return {
      wcim: b.wcim || "",
      wtanulas: b.wtanulas || "",
      wmiertte: b.wmiertte || "",
      wcta: b.wcta || "",
      phase: b.phase || "opening",
      vazlat: b.vazlat || null,
      vazlatRaw: b.vazlatRaw || "",
      vazlatJobId: b.vazlatJobId || null,
      openingResult: b.openingResult || null,
    };
  };
  const [buckets, setBuckets] = useState(() => {
    // Draft-perzisztálás: a részben vagy teljesen generált webinár
    // állapot (input mezők, phase, vázlat, vázlatRaw, openingResult) —
    // MINDEN patchBucket után localStorage-be íródik variant-kulcsú
    // draftként. Így véletlen refresh, route-váltás vagy session
    // vissza-térés után az user pontosan ott folytathatja, ahol
    // abbahagyta. A draft NEM Tudástár-elem; csak akkor kerül a
    // Tudástárba, ha a user kifejezetten rákattint a „Mentés a
    // Tudástárba" gombra. Mount-kor visszatöltjük a draftot; save
    // után explicit clear (lásd `clearVariantDraft` a Carousel
    // onSaved callback-jában).
    const base = { callbook_funnel: emptyBucket(), direct_sales: emptyBucket() };
    if (typeof window === "undefined") return base;
    try {
      const raw = window.localStorage.getItem("mex_webinar_agent_draft_v1");
      if (!raw) return base;
      const parsed = JSON.parse(raw);
      const merge = (variant) => {
        const d = parsed?.[variant];
        if (!d || typeof d !== "object") return emptyBucket();
        // Ha a draft még az „opening" input képernyőn volt (azaz a user
        // csak írogatott az input mezőkbe, de MÉG NEM indított
        // vázlat/webinár generálást), akkor NE töltsük vissza a
        // félig kitöltött form-ot. A user szándékos kilépése esetén
        // (másik kártya / Műhely / Dashboard) a mezőknek tisztán kell
        // állniuk, amikor visszatér. Csak akkor őrizzük meg a draftot,
        // ha már van érdemi munka (vázlat / carousel phase).
        if (!d.phase || d.phase === "opening") return emptyBucket();
        return { ...emptyBucket(), ...d,
          // Loading flag-eket ne perzisztáljuk — refresh után nincs
          // aktív polling; a user egyszerűen a „vazlat" képernyőn
          // folytatja.
          vazlatLoading: false, vazlatBtnLabel: "",
          openingLoading: false, openingLabel: "",
        };
      };
      return {
        callbook_funnel: merge("callbook_funnel"),
        direct_sales: merge("direct_sales"),
      };
    } catch {
      return base;
    }
  });
  // Draft-mentés localStorage-be a bucket-változásokkor. Csak a
  // strukturális mezőket mentjük (loading flag-eket, hibaszövegeket
  // nem — ezek refresh után úgyis irrelevánsak).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const snapshot = {
      callbook_funnel: pickDraft(buckets.callbook_funnel),
      direct_sales: pickDraft(buckets.direct_sales),
    };
    try {
      window.localStorage.setItem("mex_webinar_agent_draft_v1", JSON.stringify(snapshot));
    } catch { /* quota / private mode noop */ }
  }, [buckets]);
  const bucket = buckets[agentVariant] || buckets.callbook_funnel;
  const patchBucket = (variant, patch) => {
    setBuckets((prev) => ({ ...prev, [variant]: { ...prev[variant], ...patch } }));
  };
  const patchActive = (patch) => patchBucket(agentVariant, patch);
  // Draft törlés variant-specifikusan — a Tudástárba mentés UTÁN, vagy
  // amikor a user explicit „új webinár" flow-t indít.
  const clearVariantDraft = (variant) => {
    setBuckets((prev) => ({ ...prev, [variant]: emptyBucket() }));
  };

  // Per-variant read aliasok — a lenti JSX/handler kód a bucket-ből
  // olvassa a mezőket, és a patchBucket-en keresztül ír.
  const wcim = bucket.wcim;
  const wtanulas = bucket.wtanulas;
  const wmiertte = bucket.wmiertte;
  const wcta = bucket.wcta;
  const phase = bucket.phase;
  const vazlatLoading = bucket.vazlatLoading;
  const vazlatBtnLabel = bucket.vazlatBtnLabel;
  const vazlatError = bucket.vazlatError;
  const vazlat = bucket.vazlat;
  const vazlatRaw = bucket.vazlatRaw;
  const vazlatJobId = bucket.vazlatJobId;
  const openingLoading = bucket.openingLoading;
  const openingLabel = bucket.openingLabel;
  const openingResult = bucket.openingResult;

  // Q04 mező opcionális direct_sales agent esetén (user spec: ha üres,
  // vagy „az Ajánlatból dolgozz", akkor az F3 Ajánlat lesz a fő
  // forrás). Callback_funnel-nél változatlanul kötelező (stratégiai
  // hívás explicit célja).
  const _isDirect = agentVariant === "direct_sales";
  const canGenerate = wcim.trim() && wtanulas.trim() && wmiertte.trim() && (_isDirect || wcta.trim());

  const inputs = {
    webinar_cim: wcim.trim(),
    mit_tanuljon: wtanulas.trim(),
    miert_te: wmiertte.trim(),
    hova_hivjuk: wcta.trim(),
  };

  // ── Phase 1 · Vázlat generálás (B dok 2-3): no fullscreen loader,
  //    gomb feliratai rotálnak: Vázlat készül… / Nagy ötlet kidolgozása…
  //    A function expressziókat felülről bevisszük a render előtt.
  //    A `variant` capture biztosítja: ha közben a user átvált másik
  //    sub-agentre, akkor is a saját bucket-jébe íródik vissza az
  //    eredmény, és nem szennyezi a másik variant-et.
  const startVazlatGeneration = async () => {
    if (!canGenerate || vazlatLoading) return;
    const variant = agentVariant;
    patchBucket(variant, { vazlatLoading: true, vazlatError: "", vazlatBtnLabel: "Vázlat készül…" });
    const stages = [
      "Vázlat készül…",
      "Nagy ötlet kidolgozása…",
      "3 győzelem összeállítása…",
      "Telefonhívás íve készül…",
      "Vázlat véglegesítése…",
    ];
    let stageIdx = 0;
    const stageTimer = setInterval(() => {
      stageIdx = (stageIdx + 1) % stages.length;
      patchBucket(variant, { vazlatBtnLabel: stages[stageIdx] });
    }, 10000);
    try {
      const activeAlap = getActiveAlap();
      const startRes = await api.post(
        "/conversion/webinar/vazlat/generate-async",
        {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
          agent_variant: variant,
          ...inputs,
        },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a vázlat generálást.");
      patchBucket(variant, { vazlatJobId: jobId });
      // Poll the job
      let done = false;
      for (let attempt = 0; attempt < 30 && !done; attempt++) {
        await new Promise((r) => setTimeout(r, 4000));
        const pollRes = await api.get(`/conversion/webinar/callbook-funnel/job/${jobId}`);
        const status = pollRes.data?.status;
        if (status === "done") {
          const r = pollRes.data.result || {};
          done = true;
          clearInterval(stageTimer);
          patchBucket(variant, {
            vazlat: r.vazlat || null,
            vazlatRaw: r.vazlat_raw || "",
            vazlatLoading: false,
            vazlatBtnLabel: "",
            phase: "vazlat",
          });
          return;
        }
        if (status === "error") {
          throw new Error(pollRes.data?.error || "A vázlat generálása sikertelen.");
        }
      }
      if (!done) throw new Error("Időtúllépés — a vázlat generálás túl sokáig tart.");
    } catch (e) {
      clearInterval(stageTimer);
      const msg = e?.response?.data?.detail || e?.message || "A vázlat generálása sikertelen.";
      patchBucket(variant, { vazlatError: msg, vazlatLoading: false, vazlatBtnLabel: "" });
    }
  };

  // ── Opening (1. szekció) generálás → utána carousel képernyő ────
  // A user a vázlatképernyő "Teljes webinár generálása" gombra kattint:
  // ez az opening szekciót indítja el aszinkron, közben a lila gombon
  // rotáló szekció-státusz feliratok futnak (v7 §16). Amint az opening
  // kész, átváltunk a Carousel képernyőre, ahol az opening már nyitva,
  // a többi szekció pedig háttérben sorrendben generálódik.
  const startWebinarGeneration = async () => {
    if (openingLoading || !vazlatRaw) return;
    const variant = agentVariant;
    patchBucket(variant, {
      openingLoading: true,
      openingResult: null,
      openingLabel: "Első szekció készül…",
    });
    const stages = [
      "Első szekció készül…",
      "Horog és ígéret kidolgozása…",
      "Eredet történet építése…",
      "Nagy ötlet és kontraszt készül…",
      "Első szekció véglegesítése…",
    ];
    let stageIdx = 0;
    const stageTimer = setInterval(() => {
      stageIdx = (stageIdx + 1) % stages.length;
      patchBucket(variant, { openingLabel: stages[stageIdx] });
    }, 8000);
    try {
      const activeAlap = getActiveAlap();
      const startRes = await api.post(
        "/conversion/webinar/section/generate-async",
        {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
          section_id: "opening",
          agent_variant: variant,
          ...inputs,
          vazlat_raw: vazlatRaw,
          vazlat: vazlat || null,
          previous_summaries: [],
        },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a generálást.");
      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 3500));
        const pollRes = await api.get(`/conversion/webinar/section/job/${jobId}`);
        const status = pollRes.data?.status;
        if (status === "done" && pollRes.data?.result) {
          clearInterval(stageTimer);
          patchBucket(variant, {
            openingResult: pollRes.data.result,
            openingLoading: false,
            openingLabel: "",
            phase: "carousel",
          });
          return;
        }
        if (status === "error") {
          throw new Error(pollRes.data?.error || "Az első szekció generálása sikertelen.");
        }
      }
      throw new Error("Időtúllépés — az első szekció generálása túl sokáig tart.");
    } catch (e) {
      clearInterval(stageTimer);
      const msg = e?.response?.data?.detail || e?.message || "Az első szekció generálása sikertelen.";
      patchBucket(variant, { openingLoading: false, openingLabel: "" });
      // toast lent a WebinarVazlatScreen kezeli; itt a vázlatra esünk vissza
      // egy soft error-üzenettel
      try { (await import("sonner")).toast.error(msg); } catch { /* noop */ }
    }
  };

  if (phase === "carousel") {
    return (
      <WebinarSectionCarousel
        key={agentVariant}
        inputs={inputs}
        vazlat={vazlat}
        vazlatRaw={vazlatRaw}
        initialOpening={openingResult}
        onClose={() => patchActive({ phase: "vazlat" })}
        agentVariant={agentVariant}
        onSavedToVault={() => clearVariantDraft(agentVariant)}
      />
    );
  }

  if (phase === "vazlat" && vazlat) {
    return (
      <WebinarVazlatScreen
        key={agentVariant}
        agentVariant={agentVariant}
        vazlat={vazlat}
        vazlatRaw={vazlatRaw}
        jobId={vazlatJobId}
        inputs={inputs}
        writerLoading={openingLoading}
        writerLoadingLabel={openingLabel}
        onVazlatRawChange={(newRaw, newParsed) => {
          patchActive({
            vazlatRaw: newRaw,
            ...(newParsed ? { vazlat: newParsed } : {}),
          });
        }}
        onStartWriter={startWebinarGeneration}
        onBack={() => patchActive({ phase: "opening" })}
      />
    );
  }

  return (
    <AgentOpeningScreen
      eyebrow="ÉRTÉKESÍTÉS · 01 · WEBINÁR"
      title="Webinár Varázsló"
      subtitle="Szia! Segítek olyan online előadást építeni, amely felépíti a vágyat az ajánlatod iránt, és tiszta döntési helyzetet teremt."
      options={options}
      columns={3}
      testidPrefix="webinar"
      accent={GOLD_ACCENT}
      controlledSelected={selected}
      onSelectOption={(opt) => {
        if (!opt || opt.locked) return;
        setSelected(opt.id);
      }}
      belowGrid={showCallbookFunnelSetup ? (
        <WebinarCallbookFunnelSetup
          key={agentVariant}
          agentVariant={agentVariant}
          webinarCim={wcim}                 setWebinarCim={(v) => patchActive({ wcim: v })}
          webinarTanulas={wtanulas}         setWebinarTanulas={(v) => patchActive({ wtanulas: v })}
          webinarMiertTe={wmiertte}         setWebinarMiertTe={(v) => patchActive({ wmiertte: v })}
          webinarCta={wcta}                 setWebinarCta={(v) => patchActive({ wcta: v })}
          canGenerate={!!canGenerate && !vazlatLoading}
          loading={vazlatLoading}
          loadingLabel={vazlatBtnLabel}
          errorMsg={vazlatError}
          onGenerate={startVazlatGeneration}
        />
      ) : null}
    />
  );
}

/* ─── 01b · "Webinárból telefonhívás" intake ─────────────────────────
   A meglévő `Webinár promó` input képernyő (AttractionAgents →
   WebinarInlineSetup) felépítését másoljuk: eyebrow label + input/textarea
   + halvány note style. A különbség csak: gold (Konverzió) accent és
   pontosan 4 mező. Avatar / Ajánlat / Márkahang / stílus / megközelítési
   szög KÉRDÉS NINCS — a Writer prompt szintjén injektálódnak. */
function WebinarCallbookFunnelSetup({
  webinarCim, setWebinarCim,
  webinarTanulas, setWebinarTanulas,
  webinarMiertTe, setWebinarMiertTe,
  webinarCta, setWebinarCta,
  canGenerate = false,
  loading = false,
  loadingLabel = "",
  errorMsg = "",
  onGenerate = () => {},
  agentVariant = "callbook_funnel",
}) {
  const handleClear = () => {
    if (loading) return;
    setWebinarCim("");
    setWebinarTanulas("");
    setWebinarMiertTe("");
    setWebinarCta("");
  };
  // Q04 variant delta: a "Webinárból telefonhívás" (callbook_funnel)
  // agent 4. kérdése a stratégiai hívásra vezet; a "Közvetlen
  // értékesítés" (direct_sales) agent 4. kérdése a webinár végén
  // eladott programot / ajánlatot kéri be. Minden más 1-3 mező
  // (cím / mit tanuljon / miért te) bit-szinten változatlan mindkét
  // sub-agentnél.
  const isDirect = agentVariant === "direct_sales";
  const q04Label = isDirect
    ? "04 · MIT ADUNK EL A WEBINÁR VÉGÉN?"
    : "04 · HOVA HÍVJUK MEG A WEBINÁR VÉGÉN?";
  const q04Placeholder = isDirect
    ? "Írd le röviden, mit adunk el a webinár végén. Ha az Ajánlatban már minden megvan, hagyd üresen vagy írd be: az Ajánlatból dolgozz. Ide csak akkor írj plusz információt, ha módosítani vagy kiegészíteni szeretnéd az ajánlatot: név, ár, fizetési opció, bónuszok, határidő, garancia, fő CTA."
    : "Pl.: stratégiai hívásra, ahol összerakjuk a mestertervét.";
  const eyebrowStyle = {
    fontSize: 11, color: GOLD_ACCENT.eyebrow, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 18,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };

  return (
    <div
      className="mx-auto"
      style={{
        width: "100%", maxWidth: 720, padding: "0 8px",
        textAlign: "left",
        marginTop: 80, marginBottom: 36,
      }}
      data-testid="webinar-callbook-funnel-questions"
    >
      {/* 01 — WEBINÁR CÍME / TÉMÁJA */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
          <label style={{ ...eyebrowStyle, marginBottom: 0, flex: 1, minWidth: 0 }}>01 · WEBINÁR CÍME / TÉMÁJA</label>
          <WizardClearButton onClick={handleClear} clearing={loading} accent={GOLD_ACCENT.eyebrow} testid="webinar-callbook-clear-btn" />
        </div>
        <input
          type="text"
          value={webinarCim}
          onChange={(e) => setWebinarCim(e.target.value)}
          placeholder="Pl.: Hogyan szerezz kiszámítható ügyfeleket napi posztolás nélkül?"
          data-testid="webinar-callbook-funnel-cim-input"
          style={inputStyle}
        />
      </div>

      {/* 02 — MIT TANÍTSON MEG A WEBINÁR? */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>02 · MIT TANÍTSON MEG A WEBINÁR?</label>
        <textarea
          value={webinarTanulas}
          onChange={(e) => setWebinarTanulas(e.target.value)}
          placeholder="Írd le pár mondatban, mit értsen meg a néző, milyen 3 dolgot lásson tisztábban a végére."
          rows={3}
          data-testid="webinar-callbook-funnel-tanulas-input"
          style={inputStyle}
        />
      </div>

      {/* 03 — MIÉRT TE VAGY A MEGFELELŐ EMBER ERRE? */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>03 · MIÉRT TE VAGY A MEGFELELŐ EMBER ERRE?</label>
        <textarea
          value={webinarMiertTe}
          onChange={(e) => setWebinarMiertTe(e.target.value)}
          placeholder="Írj 2-4 rövid pontot: saját sztori, ügyféltapasztalat, szakmai háttér, fordulópont vagy felismerés, ami megkülönböztet."
          rows={4}
          data-testid="webinar-callbook-funnel-miertte-input"
          style={inputStyle}
        />
      </div>

      {/* 04 — Q04 · variant delta */}
      <div style={{ marginBottom: 8 }}>
        <label style={eyebrowStyle}>{q04Label}</label>
        {isDirect ? (
          <textarea
            value={webinarCta}
            onChange={(e) => setWebinarCta(e.target.value)}
            placeholder={q04Placeholder}
            rows={4}
            data-testid="webinar-callbook-funnel-cta-input"
            style={inputStyle}
          />
        ) : (
          <input
            type="text"
            value={webinarCta}
            onChange={(e) => setWebinarCta(e.target.value)}
            placeholder={q04Placeholder}
            data-testid="webinar-callbook-funnel-cta-input"
            style={inputStyle}
          />
        )}
      </div>

      {/* Generálás CTA — lila rendszer-gomb (MagicGenerateButton),
          IDENTIKUS a többi ügynök gombjával. Loading közben dinamikusan
          rotál a felirat (vázlat készül… → nagy ötlet kidolgozása… → …
          → vázlat véglegesítése…), és NINCS külön fullscreen loader. */}
      <div style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <MagicGenerateButton
          state={loading ? "generating" : (canGenerate ? "selected" : "idle")}
          label={loading ? (loadingLabel || "Vázlat készül…") : "Webinár vázlat generálása"}
          onClick={() => { if (canGenerate && !loading) onGenerate(); }}
          disabled={!canGenerate && !loading}
          testid="webinar-callbook-funnel-generate-btn"
        />
        {errorMsg ? (
          <div data-testid="webinar-callbook-funnel-error-inline" style={{
            fontSize: 12.5, color: "#ffb8b8", marginTop: 6, textAlign: "center", maxWidth: 520,
          }}>
            {errorMsg}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ─── 02 · Landing Varázsló ─────────────────────────────────────────── */
// A Landing Varázsló wrapper komponense. Két módban működik:
//  1) Tudástár read-only viewer: ha `location.state.savedItem` van +
//     `agent_type === "landing-webinar-registration"`, a `WebinarSectionCarousel`-t
//     tölti be read-only módban a mentett `carousel_content`-tel. A
//     `LANDING_API_ENDPOINTS`-t adja át (nem érinti a webinár default-okat).
//  2) Full wizard: az opening screen 3 kártyát mutat, "webinar" (Regisztrációs
//     oldal) kiválasztása után betölti a `LandingRegistrationInner`
//     komponenst, ami a webinár flow IDENTIKUS klónja SAJÁT endpointokkal,
//     SAJÁT localStorage draft-kulccsal, SAJÁT agent_type mentéssel.
//     A "magnet" (Letöltő) és "sales" (Sales) kártyák egyelőre no-op-ok —
//     későbbi körben építjük.

// Landing endpoint-ok — teljes leválasztás a webinar endpointoktól.
// A WebinarSectionCarousel és WebinarVazlatScreen komponensek fogadják
// az `apiEndpoints` propot, ami override-olja a default webinar URL-eket.
const LANDING_API_ENDPOINTS = {
  vazlatGenerateAsync: "/conversion/landing-registration/vazlat/generate-async",
  vazlatJob: (jobId) => `/conversion/landing-registration/vazlat/job/${jobId}`,
  sectionGenerateAsync: "/conversion/landing-registration/section/generate-async",
  sectionJob: (jobId) => `/conversion/landing-registration/section/job/${jobId}`,
  refine: "/conversion/landing-registration/section/refine",
  save: "/conversion/landing-registration/save",
};

export function LandingAgent() {
  const location = useLocation();
  const navigate = useNavigate();
  const savedItem = location?.state?.savedItem || null;
  const savedBlueprint = savedItem?.blueprint || null;
  const savedCarouselContent = savedBlueprint?.carousel_content || null;

  // Tudástár read-only viewer: a Landing típusú mentett anyagok ide
  // érkeznek `state.savedItem`-el. A Carousel `readOnly` módban jeleníti
  // meg a mentett slide-okat — nincs regenerálás.
  if (savedItem && savedCarouselContent && savedItem.agent_type === "landing-webinar-registration") {
    return (
      <WebinarSectionCarousel
        key={`vault-landing-${savedItem.id}`}
        inputs={{ webinar_cim: savedItem.name || savedBlueprint?.webinar_cim || "" }}
        vazlat={null}
        vazlatRaw=""
        initialOpening={null}
        agentVariant="landing_registration"
        savedCarouselContent={savedCarouselContent}
        readOnly={true}
        onClose={() => navigate("/asset-vault")}
        onSavedToVault={null}
        apiEndpoints={LANDING_API_ENDPOINTS}
      />
    );
  }

  return <LandingAgentInner />;
}

// LandingAgentInner — teljes Landing Varázsló flow EGY komponensben.
// A UI mintát a `WebinarAgentInner` követi: az `AgentOpeningScreen`
// egy példányban van renderelve a 3 típuskártyával (magnet locked +
// webinar aktív + sales locked), és a kiválasztott `webinar` kártya
// alatt a `belowGrid` propon keresztül JELENIK MEG az input panel —
// NEM külön oldal, NEM duplikált kártya, egy kattintás elég.
//
// A `LandingRegistrationInner` komponens megszűnt (a state és a
// generation logika ebbe a komponensbe került, hogy egyetlen
// `AgentOpeningScreen` render legyen). A phase alapú `WebinarVazlatScreen`
// és `WebinarSectionCarousel` render változatlan.
function LandingAgentInner() {
  const [selected, setSelected] = useState(null);
  // Landing Varázsló 3 kártya: Regisztrációs oldal (nyitva), Köszönőoldal
  // (nyitva, saját flow), Sales oldal (Hamarosan). A „magnet" (Letöltő
  // oldal) korábbi placeholder kártyája kikerült — a spec 3 kártyát ír le.
  const options = [
    { id: "webinar",  title: "Regisztrációs oldal", label: "Oldal, ami elkötelezi a nézőt a megjelenésre",             icon: Microphone },
    { id: "thankyou", title: "Köszönőoldal",         label: "Automatikusan a regisztrációs oldalból, megerősítés és következő lépés", icon: Gift },
    { id: "sales",    title: "Sales oldal",          label: "Értékesítési oldal, ami a döntésig vezeti az olvasót",    icon: CurrencyDollar },
  ];

  // ── State + endpoint set (az egykori LandingRegistrationInner-ből) ──
  const emptyBucket = () => ({
    wcim: "", wtanulas: "", wmiertte: "", wcta: "",
    phase: "opening",
    vazlatLoading: false, vazlatBtnLabel: "", vazlatError: "",
    // Landing új flow: 3 cím-alcím opció + kiválasztott
    titleOptions: null, chosenTitle: null,
    vazlatRaw: "", vazlatJobId: null,
    // Landing page-generation result state (új dedikált flow)
    pageLoading: false, pageLabel: "", pageResult: null,
  });
  const pickDraft = (b) => {
    if (!b) return null;
    return {
      wcim: b.wcim || "", wtanulas: b.wtanulas || "",
      wmiertte: b.wmiertte || "", wcta: b.wcta || "",
      phase: b.phase || "opening",
      titleOptions: b.titleOptions || null,
      chosenTitle: b.chosenTitle || null,
      vazlatRaw: b.vazlatRaw || "",
      vazlatJobId: b.vazlatJobId || null,
      pageResult: b.pageResult || null,
    };
  };
  const [bucket, setBucket] = useState(() => {
    if (typeof window === "undefined") return emptyBucket();
    try {
      const raw = window.localStorage.getItem("mex_landing_registration_agent_draft_v1");
      if (!raw) return emptyBucket();
      const d = JSON.parse(raw);
      if (!d || typeof d !== "object") return emptyBucket();
      if (!d.phase || (d.phase === "opening" && !d.pageResult)) return emptyBucket();
      return { ...emptyBucket(), ...d,
        vazlatLoading: false, vazlatBtnLabel: "",
        pageLoading: false, pageLabel: "",
      };
    } catch {
      return emptyBucket();
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        "mex_landing_registration_agent_draft_v1",
        JSON.stringify(pickDraft(bucket)),
      );
    } catch { /* noop */ }
  }, [bucket]);
  const patch = (p) => setBucket((prev) => ({ ...prev, ...p }));
  const clearDraft = () => setBucket(emptyBucket());

  const wcim = bucket.wcim, wtanulas = bucket.wtanulas;
  const wmiertte = bucket.wmiertte, wcta = bucket.wcta;
  const phase = bucket.phase;
  const {
    vazlatLoading, vazlatBtnLabel, vazlatError,
    titleOptions, chosenTitle,
    vazlatRaw, vazlatJobId,
    pageLoading, pageLabel, pageResult,
  } = bucket;

  const canGenerate = wcim.trim() && wtanulas.trim() && wmiertte.trim();
  const inputs = {
    webinar_cim: wcim.trim(),
    mit_tanuljon: wtanulas.trim(),
    miert_te: wmiertte.trim(),
    hova_hivjuk: wcta.trim(),
  };

  const startVazlatGeneration = async () => {
    if (!canGenerate || vazlatLoading) return;
    patch({ vazlatLoading: true, vazlatError: "", vazlatBtnLabel: "Webinárcímek készülnek…" });
    const stages = [
      "Webinárcímek készülnek…",
      "Eredményközpontú irány kidolgozása…",
      "Problémafelismerés irány kidolgozása…",
      "Nézőpontváltó irány kidolgozása…",
      "Címek véglegesítése…",
    ];
    let idx = 0;
    const timer = setInterval(() => {
      idx = (idx + 1) % stages.length;
      patch({ vazlatBtnLabel: stages[idx] });
    }, 10000);
    try {
      const activeAlap = getActiveAlap();
      const startRes = await api.post(LANDING_API_ENDPOINTS.vazlatGenerateAsync, {
        voice_id: activeAlap.voice || null,
        client_id: activeAlap.client || null,
        offer_id: activeAlap.offer || null,
        ...inputs,
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a vázlat generálást.");
      patch({ vazlatJobId: jobId });
      let done = false;
      for (let attempt = 0; attempt < 30 && !done; attempt++) {
        await new Promise((r) => setTimeout(r, 4000));
        const pollRes = await api.get(LANDING_API_ENDPOINTS.vazlatJob(jobId));
        const status = pollRes.data?.status;
        if (status === "done") {
          const r = pollRes.data.result || {};
          done = true;
          clearInterval(timer);
          // Landing új flow: 3 cím-alcím opció betöltése (nem strategic vázlat)
          patch({
            titleOptions: r.title_options || [],
            vazlatRaw: r.vazlat_raw || "",
            vazlatLoading: false, vazlatBtnLabel: "",
            phase: "vazlat",
          });
          return;
        }
        if (status === "error") throw new Error(pollRes.data?.error || "A vázlat generálása sikertelen.");
      }
      if (!done) throw new Error("Időtúllépés — a vázlat generálás túl sokáig tart.");
    } catch (e) {
      clearInterval(timer);
      const msg = e?.response?.data?.detail || e?.message || "A vázlat generálása sikertelen.";
      patch({ vazlatError: msg, vazlatLoading: false, vazlatBtnLabel: "" });
    }
  };

  const startPageGeneration = async (chosen) => {
    if (pageLoading) return;
    const chosenSafe = chosen || chosenTitle;
    if (!chosenSafe || !chosenSafe.title) return;
    patch({
      pageLoading: true, pageResult: null,
      pageLabel: "Regisztrációs oldal készül…",
      chosenTitle: chosenSafe,
    });
    const stages = [
      "Regisztrációs oldal készül…",
      "Hero szekció írása…",
      "Tanítások és empátia szakasz…",
      "„Neked szól\" és „Miért más\" szakasz…",
      "Előadó és zárás véglegesítése…",
    ];
    let idx = 0;
    const timer = setInterval(() => {
      idx = (idx + 1) % stages.length;
      patch({ pageLabel: stages[idx] });
    }, 8000);
    try {
      const activeAlap = getActiveAlap();
      const startRes = await api.post(
        "/conversion/landing-registration/page/generate-async",
        {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
          chosen_title: {
            id: chosenSafe.id,
            direction: chosenSafe.direction || "",
            webinar_name: chosenSafe.webinar_name || "",
            title: chosenSafe.title || "",
            subtitle: chosenSafe.subtitle || "",
          },
          ...inputs,
        },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a generálást.");
      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 3500));
        const pollRes = await api.get(`/conversion/landing-registration/page/job/${jobId}`);
        const status = pollRes.data?.status;
        if (status === "done" && pollRes.data?.result) {
          clearInterval(timer);
          patch({
            pageResult: pollRes.data.result,
            pageLoading: false, pageLabel: "",
            phase: "result",
          });
          return;
        }
        if (status === "error") throw new Error(pollRes.data?.error || "A generálás sikertelen.");
      }
      throw new Error("Időtúllépés — a generálás túl sokáig tart.");
    } catch (e) {
      clearInterval(timer);
      const msg = e?.response?.data?.detail || e?.message || "Regisztrációs oldal generálás sikertelen.";
      patch({ pageLoading: false, pageLabel: "" });
      try { (await import("sonner")).toast.error(msg); } catch { /* noop */ }
    }
  };

  // ── KÖSZÖNŐOLDAL FLOW (külön bucket, külön localStorage, külön endpointok) ──
  // A Köszönőoldal-flow teljesen külön van kezelve a Regisztrációs
  // oldal draftjától. Ne érintse a `bucket`-et vagy a Regisztrációs
  // localStorage kulcsot. A generáláshoz a Regisztrációs oldal
  // aktuális, ténylegesen szerkesztett `pageResult`-jét adjuk át
  // `source_page` propon — nem generálódik újra a cím vagy vázlat.
  const emptyThankyou = () => ({
    phase: "opening",       // opening → result
    pageLoading: false, pageLabel: "",
    pageResult: null,       // Köszönőoldal generálás kimenete
    sourcePageId: null,     // ha egy mentett Regisztrációs oldalból indult
  });
  const [thankyouBucket, setThankyouBucket] = useState(() => {
    if (typeof window === "undefined") return emptyThankyou();
    try {
      const raw = window.localStorage.getItem("mex_landing_thank_you_agent_draft_v1");
      if (!raw) return emptyThankyou();
      const d = JSON.parse(raw);
      if (!d || typeof d !== "object") return emptyThankyou();
      if (!d.phase || d.phase === "opening") return emptyThankyou();
      return { ...emptyThankyou(), ...d, pageLoading: false, pageLabel: "" };
    } catch {
      return emptyThankyou();
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        "mex_landing_thank_you_agent_draft_v1",
        JSON.stringify({
          phase: thankyouBucket.phase,
          pageResult: thankyouBucket.pageResult,
          sourcePageId: thankyouBucket.sourcePageId,
        }),
      );
    } catch { /* noop */ }
  }, [thankyouBucket]);
  const patchThankyou = (p) => setThankyouBucket((prev) => ({ ...prev, ...p }));
  const clearThankyouDraft = () => setThankyouBucket(emptyThankyou());

  // A Köszönőoldal gombja akkor aktív, ha van használható Regisztrációs
  // oldal state a jelenlegi Landing bucket-ben. Ellenőrizzük a Hero
  // tartalmi elemeit: legalább főcím + alcím + valamilyen webinárnév.
  const hasUsableRegistrationPage = (() => {
    const p = pageResult;
    if (!p || typeof p !== "object" || !p.hero) return false;
    const h = p.hero;
    const title = (h.title || "").trim();
    const subtitle = (h.subtitle || "").trim();
    const wn = (h.webinar_name || "").trim();
    return !!(title && subtitle && (wn || title));
  })();

  const startThankyouGeneration = async () => {
    if (!hasUsableRegistrationPage || thankyouBucket.pageLoading) return;
    patchThankyou({ pageLoading: true, pageLabel: "Köszönőoldal készül…" });
    const stages = [
      "Köszönőoldal készül…",
      "Hero szekció írása…",
      "Következő lépések összeállítása…",
      "Zárás véglegesítése…",
    ];
    let idx = 0;
    const timer = setInterval(() => {
      idx = (idx + 1) % stages.length;
      patchThankyou({ pageLabel: stages[idx] });
    }, 8000);
    try {
      const activeAlap = getActiveAlap();
      const startRes = await api.post(
        "/conversion/landing-thank-you/page/generate-async",
        {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
          chosen_title: {
            id: chosenTitle?.id,
            direction: chosenTitle?.direction || "",
            webinar_name: chosenTitle?.webinar_name || "",
            title: chosenTitle?.title || "",
            subtitle: chosenTitle?.subtitle || "",
          },
          ...inputs,
          source_page: pageResult,
          source_page_id: null,
        },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a köszönőoldal generálását.");
      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 3500));
        const pollRes = await api.get(`/conversion/landing-thank-you/page/job/${jobId}`);
        const status = pollRes.data?.status;
        if (status === "done" && pollRes.data?.result) {
          clearInterval(timer);
          patchThankyou({
            pageResult: pollRes.data.result,
            pageLoading: false, pageLabel: "",
            phase: "result",
          });
          return;
        }
        if (status === "error") throw new Error(pollRes.data?.error || "A generálás sikertelen.");
      }
      throw new Error("Időtúllépés — a generálás túl sokáig tart.");
    } catch (e) {
      clearInterval(timer);
      const msg = e?.response?.data?.detail || e?.message || "Köszönőoldal generálás sikertelen.";
      patchThankyou({ pageLoading: false, pageLabel: "" });
      try { (await import("sonner")).toast.error(msg); } catch { /* noop */ }
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // SALES OLDAL FLOW (külön bucket, külön localStorage, külön endpointok)
  // ═══════════════════════════════════════════════════════════════════
  const emptySales = () => ({
    phase: "opening",                 // opening → vazlat → result
    // inputok
    mit_adunk_el: "", bizonyitek: "", garancia: "", surgosseg: "",
    oldal_celja: "",  forgalom_tipus: "",
    // vázlat
    vazlatLoading: false, vazlatBtnLabel: "", vazlatError: "",
    vazlatOptions: null,
    chosenTitle: null,
    // teljes oldal
    pageLoading: false, pageLabel: "",
    pageResult: null,
  });
  const [salesBucket, setSalesBucket] = useState(() => {
    if (typeof window === "undefined") return emptySales();
    try {
      const raw = window.localStorage.getItem("mex_landing_sales_agent_draft_v1");
      if (!raw) return emptySales();
      const d = JSON.parse(raw);
      if (!d || typeof d !== "object") return emptySales();
      // Ha van pageResult, megőrizzük akkor is, ha phase közben opening-re állt.
      return { ...emptySales(), ...d, vazlatLoading: false, pageLoading: false, vazlatBtnLabel: "", pageLabel: "" };
    } catch { return emptySales(); }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        "mex_landing_sales_agent_draft_v1",
        JSON.stringify({
          phase: salesBucket.phase,
          mit_adunk_el: salesBucket.mit_adunk_el,
          bizonyitek: salesBucket.bizonyitek,
          garancia: salesBucket.garancia,
          surgosseg: salesBucket.surgosseg,
          oldal_celja: salesBucket.oldal_celja,
          forgalom_tipus: salesBucket.forgalom_tipus,
          vazlatOptions: salesBucket.vazlatOptions,
          chosenTitle: salesBucket.chosenTitle,
          pageResult: salesBucket.pageResult,
        }),
      );
    } catch { /* noop */ }
  }, [salesBucket]);
  const patchSales = (p) => setSalesBucket((prev) => ({ ...prev, ...p }));

  const salesInputsForBackend = {
    mit_adunk_el: salesBucket.mit_adunk_el,
    bizonyitek:   salesBucket.bizonyitek,
    garancia:     salesBucket.garancia,
    surgosseg:    salesBucket.surgosseg,
    oldal_celja:  salesBucket.oldal_celja,
    forgalom_tipus: salesBucket.forgalom_tipus,
  };

  const startSalesVazlatGeneration = async () => {
    if (salesBucket.vazlatLoading) return;
    if (!salesBucket.oldal_celja || !salesBucket.forgalom_tipus) {
      patchSales({ vazlatError: "Válaszd ki az oldal célját és a forgalom típusát." });
      return;
    }
    patchSales({ vazlatLoading: true, vazlatError: "", vazlatBtnLabel: "Címek készülnek…" });
    try {
      const activeAlap = getActiveAlap();
      const start = await api.post("/conversion/landing-sales/vazlat/generate-async", {
        voice_id: activeAlap.voice || null,
        client_id: activeAlap.client || null,
        offer_id: activeAlap.offer || null,
        ...salesInputsForBackend,
      });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("A generálás nem indult el.");
      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        const poll = await api.get(`/conversion/landing-sales/vazlat/job/${jobId}`);
        const status = poll.data?.status;
        if (status === "done") {
          patchSales({
            vazlatOptions: poll.data.result?.title_options || [],
            vazlatLoading: false, vazlatBtnLabel: "",
            phase: "vazlat",
          });
          return;
        }
        if (status === "error") throw new Error(poll.data?.error || "A generálás sikertelen.");
      }
      throw new Error("Időtúllépés — a generálás túl sokáig tart.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Címek generálása sikertelen.";
      patchSales({ vazlatLoading: false, vazlatBtnLabel: "", vazlatError: msg });
    }
  };

  const startSalesPageGeneration = async (title) => {
    if (salesBucket.pageLoading) return;
    patchSales({ pageLoading: true, pageLabel: "Sales oldal készül…", chosenTitle: title });
    const stages = ["Sales oldal készül…", "Hero és Ajánlat írása…", "Bizonyíték és GYIK…", "Zárás véglegesítése…"];
    let idx = 0;
    const timer = setInterval(() => {
      idx = (idx + 1) % stages.length;
      patchSales({ pageLabel: stages[idx] });
    }, 8000);
    try {
      const activeAlap = getActiveAlap();
      const start = await api.post("/conversion/landing-sales/page/generate-async", {
        voice_id: activeAlap.voice || null,
        client_id: activeAlap.client || null,
        offer_id: activeAlap.offer || null,
        chosen_title: title,
        ...salesInputsForBackend,
      });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("A generálás nem indult el.");
      for (let attempt = 0; attempt < 90; attempt++) {
        await new Promise((r) => setTimeout(r, 3500));
        const poll = await api.get(`/conversion/landing-sales/page/job/${jobId}`);
        const status = poll.data?.status;
        if (status === "done" && poll.data?.result) {
          clearInterval(timer);
          patchSales({ pageResult: poll.data.result, pageLoading: false, pageLabel: "", phase: "result" });
          return;
        }
        if (status === "error") throw new Error(poll.data?.error || "A generálás sikertelen.");
      }
      throw new Error("Időtúllépés — a Sales oldal generálás túl sokáig tart.");
    } catch (e) {
      clearInterval(timer);
      const msg = e?.response?.data?.detail || e?.message || "Sales oldal generálás sikertelen.";
      patchSales({ pageLoading: false, pageLabel: "" });
      try { (await import("sonner")).toast.error(msg); } catch { /* noop */ }
    }
  };
  // ── Phase-alapú render ──────────────────────────────────────────────
  // Sales oldal (KÜLÖN a Regisztrációs / Köszönőoldaltól)
  if (salesBucket.phase === "result" && salesBucket.pageResult) {
    return (
      <LandingSalesResultScreen
        inputs={salesInputsForBackend}
        chosenTitle={salesBucket.chosenTitle}
        initialPage={salesBucket.pageResult}
        onBack={() => patchSales({ phase: "vazlat" })}
        onSavedToVault={null}
      />
    );
  }
  if (salesBucket.phase === "vazlat" && Array.isArray(salesBucket.vazlatOptions)) {
    return (
      <LandingSalesVazlatScreen
        inputs={salesInputsForBackend}
        titleOptions={salesBucket.vazlatOptions}
        onOptionsRefreshed={(opts) => patchSales({ vazlatOptions: opts })}
        onOptionChosen={(opt) => startSalesPageGeneration(opt)}
        onBack={() => patchSales({ phase: "opening" })}
      />
    );
  }

  // Köszönőoldal külön eredménykomponens (LandingThankYouResultScreen),
  // teljesen külön state + endpointok. A Regisztrációs oldal eredményt
  // NEM cseréli le.
  if (thankyouBucket.phase === "result" && thankyouBucket.pageResult) {
    return (
      <LandingThankYouResultScreen
        inputs={inputs}
        chosenTitle={chosenTitle}
        initialPage={thankyouBucket.pageResult}
        onBack={() => patchThankyou({ phase: "opening" })}
        onSavedToVault={null}
      />
    );
  }

  // Landing DEDIKÁLT eredménykomponens (nem WebinarSectionCarousel).
  if (phase === "result" && pageResult) {
    return (
      <LandingRegistrationResultScreen
        inputs={inputs}
        chosenTitle={chosenTitle}
        initialPage={pageResult}
        onBack={() => patch({ phase: "vazlat" })}
        onSavedToVault={null}
        onNavigateToThankyou={() => {
          // A Regisztrációs eredmény state MEGŐRZŐDIK. Visszaléptetjük
          // a Landing kártyaképernyőre, és a Köszönőoldal kártyát
          // auto-kijelöljük → lenyílik az aktív lila „Létrehozom a
          // regisztrációs oldalból" gomb (a user egy külön kattintással
          // indíthatja a generálást). NEM indítunk automatikus
          // generálást és NEM nyitunk vázlatképernyőt.
          patch({ phase: "opening" });
          setSelected("thankyou");
        }}
      />
    );
  }

  if (phase === "vazlat" && titleOptions && titleOptions.length > 0) {
    return (
      <LandingRegistrationVazlatScreen
        inputs={inputs}
        titleOptions={titleOptions}
        onOptionsRefreshed={(newOpts) => patch({ titleOptions: newOpts, chosenTitle: null })}
        onOptionChosen={(opt) => {
          // A kiválasztott cím-alcím a state-be kerül, majd elindul a
          // Landing PAGE generation (új dedikált 8-szekciós flow).
          startPageGeneration(opt);
        }}
        onBack={() => patch({ phase: "opening" })}
      />
    );
  }

  // ── Opening képernyő: EGYETLEN AgentOpeningScreen 3 kártyával +
  //    belowGrid-en át a setup panel a kiválasztott „webinar" kártya
  //    alatt. Ez a Webinár Varázsló mintáját követi (belowGrid alapú
  //    lenyílás). Nincs második oldal, nincs duplikált kártya, egy
  //    kattintás elég ─────────────────────────────────────────────────
  const showRegistrationSetup = selected === "webinar";
  const showThankyouStarter = selected === "thankyou";
  const showSalesSetup = selected === "sales";

  return (
    <AgentOpeningScreen
      eyebrow="ÉRTÉKESÍTÉS · 02 · OLDAL"
      title="Landing Varázsló"
      subtitle="Szia! Segítek olyan konvertáló oldalakat írni, amelyek egyértelművé teszik az értékedet, és azonnali cselekvésre ösztönzik az olvasót."
      options={options}
      columns={3}
      testidPrefix="landing"
      accent={GOLD_ACCENT}
      controlledSelected={selected}
      onSelectOption={(opt) => {
        if (!opt || opt.locked) return;
        setSelected(opt.id);
      }}
      belowGrid={showRegistrationSetup ? (
        <LandingRegistrationSetup
          key="landing-registration"
          webinarCim={wcim}                 setWebinarCim={(v) => patch({ wcim: v })}
          webinarTanulas={wtanulas}         setWebinarTanulas={(v) => patch({ wtanulas: v })}
          webinarMiertTe={wmiertte}         setWebinarMiertTe={(v) => patch({ wmiertte: v })}
          webinarCta={wcta}                 setWebinarCta={(v) => patch({ wcta: v })}
          canGenerate={!!canGenerate && !vazlatLoading}
          loading={vazlatLoading}
          loadingLabel={vazlatBtnLabel}
          errorMsg={vazlatError}
          onGenerate={startVazlatGeneration}
        />
      ) : showThankyouStarter ? (
        <LandingThankyouStarter
          key="landing-thankyou-starter"
          hasSource={hasUsableRegistrationPage}
          loading={thankyouBucket.pageLoading}
          loadingLabel={thankyouBucket.pageLabel}
          onGenerate={startThankyouGeneration}
        />
      ) : showSalesSetup ? (
        <LandingSalesSetup
          key="landing-sales-setup"
          bucket={salesBucket}
          patch={patchSales}
          onGenerate={startSalesVazlatGeneration}
        />
      ) : null}
    />
  );
}

// ── Landing Köszönőoldal starter — lenyíló kártya-alatti indítóterület ──
// Egyetlen elsődleges gomb; a felirat mindkét állapotban azonos:
// „Létrehozom a regisztrációs oldalból". A gomb disabled/aktív állapota
// kizárólag attól függ, hogy van-e használható Regisztrációs oldal
// eredmény (`hasSource`). Nincs kérdőív, inputmező, vázlatképernyő.
function LandingThankyouStarter({ hasSource, loading, loadingLabel, onGenerate }) {
  const label = loading ? (loadingLabel || "Köszönőoldal készül…") : "Létrehozom a regisztrációs oldalból";
  return (
    <div
      className="mx-auto"
      style={{
        width: "100%", maxWidth: 720, padding: "0 8px",
        textAlign: "center", marginTop: 40, marginBottom: 32,
      }}
      data-testid="landing-thankyou-starter"
    >
      <div style={{
        // Rendszerben meglévő másodlagos helper tipográfia (lásd
        // `smallNoteStyle` a Landing setup panelen).
        fontSize: 11.5, color: "#6a6a80", marginBottom: 14, fontStyle: "italic",
      }}>
        {hasSource
          ? "A kész Regisztrációs oldal adataiból, egy kattintással."
          : "Először készítsd el a Regisztrációs oldalt."}
      </div>
      <MagicGenerateButton
        state={loading ? "generating" : (hasSource ? "selected" : "idle")}
        label={label}
        onClick={() => { if (hasSource && !loading) onGenerate?.(); }}
        size="lg"
        data-testid="landing-thankyou-generate-btn"
      />
    </div>
  );
}

// ── Landing Regisztrációs Oldal setup panel ──────────────────────────
// Külön komponens (NEM a WebinarCallbookFunnelSetup) a Landing-specifikus
// Q3 (bővített placeholder) és Q4 (webinár időpontja — datetime picker +
// szabad szöveg override) mezőkkel. Az időpont-kezelés logikája az
// EmailWebinar `WebinarInlineSetup` mintáját követi (naptár + freetext,
// szabad szöveg felülírja a naptárt).
function _landingSmartDefaultDtLocal() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}T19:00`;
}
function _landingFormatHuDateTime(dtLocal) {
  if (!dtLocal) return "";
  const m = dtLocal.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return "";
  const [, y, mo, d, h, mi] = m;
  const months = ["január","február","március","április","május","június","július","augusztus","szeptember","október","november","december"];
  const days = ["vasárnap","hétfő","kedd","szerda","csütörtök","péntek","szombat"];
  const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:00`);
  const dayName = days[date.getDay()];
  return `${y}. ${months[parseInt(mo,10)-1]} ${parseInt(d,10)}. (${dayName}), ${h}:${mi}`;
}

function LandingRegistrationSetup({
  webinarCim, setWebinarCim,
  webinarTanulas, setWebinarTanulas,
  webinarMiertTe, setWebinarMiertTe,
  webinarCta, setWebinarCta,
  canGenerate = false,
  loading = false,
  loadingLabel = "",
  errorMsg = "",
  onGenerate = () => {},
}) {
  const handleClear = () => {
    if (loading) return;
    setWebinarCim("");
    setWebinarTanulas("");
    setWebinarMiertTe("");
    setWebinarCta("");
  };
  const eyebrowStyle = {
    fontSize: 11, color: GOLD_ACCENT.eyebrow, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 18,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };
  const smallNoteStyle = {
    fontSize: 11.5, color: "#6a6a80", marginTop: 6, fontStyle: "italic",
  };

  // A `webinarCta` state tárolja a végleges (formattált magyar) időpont
  // string-et, ami a backendre megy `hova_hivjuk` mezőben. A user vagy
  // a naptárból választ (auto-formattálva magyar hosszú formátumra),
  // vagy szabad szöveges mezőbe ír — ez utóbbi FELÜLÍRJA a naptárt.
  const [dtLocal, setDtLocal] = useState("");
  const idopontFreeText = webinarCta || "";
  const onDtLocalChange = (v) => {
    setDtLocal(v);
    const formatted = _landingFormatHuDateTime(v);
    if (formatted) setWebinarCta(formatted);
  };

  return (
    <div
      className="mx-auto"
      style={{
        width: "100%", maxWidth: 720, padding: "0 8px",
        textAlign: "left",
        marginTop: 80, marginBottom: 36,
      }}
      data-testid="landing-registration-questions"
    >
      {/* 01 — WEBINÁR CÍME / TÉMÁJA */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
          <label style={{ ...eyebrowStyle, marginBottom: 0, flex: 1, minWidth: 0 }}>01 · WEBINÁR CÍME / TÉMÁJA</label>
          <WizardClearButton onClick={handleClear} clearing={loading} accent={GOLD_ACCENT.eyebrow} testid="landing-registration-clear-btn" />
        </div>
        <input
          type="text"
          value={webinarCim}
          onChange={(e) => setWebinarCim(e.target.value)}
          placeholder="Pl.: Hogyan szerezz kiszámítható ügyfeleket napi posztolás nélkül?"
          data-testid="landing-registration-cim-input"
          style={inputStyle}
        />
      </div>

      {/* 02 — MIT TANÍTSON MEG A WEBINÁR? */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>02 · MIT TANÍTSON MEG A WEBINÁR?</label>
        <textarea
          value={webinarTanulas}
          onChange={(e) => setWebinarTanulas(e.target.value)}
          placeholder="Írd le pár mondatban, mit értsen meg a néző, milyen 3 dolgot lásson tisztábban a végére."
          rows={3}
          data-testid="landing-registration-tanulas-input"
          style={inputStyle}
        />
      </div>

      {/* 03 — MIÉRT TE VAGY A MEGFELELŐ EMBER ERRE? (Landing bővített placeholder) */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>03 · MIÉRT TE VAGY A MEGFELELŐ EMBER ERRE?</label>
        <textarea
          value={webinarMiertTe}
          onChange={(e) => setWebinarMiertTe(e.target.value)}
          placeholder="Írj magadról néhány gondolatot vagy mondatot, amelyet szeretnél, hogy felhasználjunk a regisztrációs oldalon. Leírhatod a témához kapcsolódó tapasztalatodat, történetedet, eredményeidet vagy azt, miért fontos számodra ez a téma."
          rows={5}
          data-testid="landing-registration-miertte-input"
          style={inputStyle}
        />
      </div>

      {/* 04 — WEBINÁR IDŐPONTJA (naptár + szabad szöveg override) */}
      <div style={{ marginBottom: 8 }}>
        <label style={eyebrowStyle}>04 · WEBINÁR IDŐPONTJA</label>
        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3">
          <input
            type="datetime-local"
            value={dtLocal}
            onChange={(e) => onDtLocalChange(e.target.value)}
            onFocus={() => {
              // Ha még nem választott a felhasználó, a picker
              // megnyitásakor preset 19:00 (perc=0, kerek óra), hogy
              // egész órás időpont esetén ne kelljen görgetni a perceket.
              // A `webinarCta` (parent state) MÉG üres marad — csak
              // akkor frissül, ha a user ténylegesen változtat.
              if (!dtLocal) setDtLocal(_landingSmartDefaultDtLocal());
            }}
            step="900"
            data-testid="landing-registration-idopont-datetime"
            style={{
              ...inputStyle,
              padding: "12px 14px",
              colorScheme: "dark",
            }}
          />
          <input
            type="text"
            value={idopontFreeText}
            onChange={(e) => setWebinarCta(e.target.value)}
            placeholder="Vagy írd saját formátumban: csütörtök 19:00"
            data-testid="landing-registration-idopont-freetext"
            style={inputStyle}
          />
        </div>
        <div style={smallNoteStyle}>
          Naptárból vagy szabad szöveggel – a szabad szöveg felülírja a naptárt.
        </div>
      </div>

      {/* Generálás CTA — ugyanaz a rendszer-gomb, mint a webinárnál */}
      <div style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <MagicGenerateButton
          state={loading ? "generating" : (canGenerate ? "selected" : "idle")}
          label={loading ? (loadingLabel || "Webinárcímek készülnek…") : "Webinárcímek generálása"}
          onClick={() => { if (canGenerate && !loading) onGenerate(); }}
          size="lg"
          data-testid="landing-registration-generate-btn"
        />
        {errorMsg ? (
          <div style={{ fontSize: 12, color: "#ff6b6b", marginTop: 4 }}>
            {errorMsg}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* Landing Sales — pill választó (kiemelve a Setup függvényből, hogy ne
   definiálódjon minden renderelésnél). */
function _SalesPill({ value, current, onClick, testid }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        padding: "10px 16px", borderRadius: 999,
        border: `1px solid ${active ? GOLD_ACCENT.strong : "rgba(255,255,255,0.10)"}`,
        background: active ? "rgba(255,219,92,0.14)" : "rgba(255,255,255,0.02)",
        color: active ? "#f2e29a" : "#c8c8d8",
        fontSize: 13, fontWeight: 500, cursor: "pointer",
        fontFamily: "inherit", transition: "all 160ms ease",
      }}
    >{value}</button>
  );
}

/* ─── Landing Varázsló · Sales oldal — Setup nézet ─────────────────────
   PIXELPONTOSAN a Regisztrációs setup mintája: sötét háttér, arany
   szekciószámok, textarea komponens, pill-választó, MagicGenerateButton.
   Csak a 6 Sales-input és a pill-választók különböznek.
*/
function LandingSalesSetup({ bucket, patch, onGenerate }) {
  const handleClear = () => {
    if (bucket.vazlatLoading) return;
    patch({
      mit_adunk_el: "",
      bizonyitek: "",
      garancia: "",
      surgosseg: "",
      oldal_celja: "",
      forgalom_tipus: "",
    });
  };
  const eyebrowStyle = {
    fontSize: 11, color: GOLD_ACCENT.eyebrow, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 18,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };
  const smallNoteStyle = { fontSize: 11.5, color: "#6a6a80", marginTop: 6, fontStyle: "italic" };

  const canGenerate = !!(bucket.oldal_celja && bucket.forgalom_tipus) && !bucket.vazlatLoading;
  const loading = bucket.vazlatLoading;

  return (
    <div className="mx-auto"
      style={{ width: "100%", maxWidth: 720, padding: "0 8px", textAlign: "left", marginTop: 80, marginBottom: 36 }}
      data-testid="landing-sales-questions"
    >
      {/* 01 — MIT ADUNK EL? */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
          <label style={{ ...eyebrowStyle, marginBottom: 0, flex: 1, minWidth: 0 }}>01 · MIT ADUNK EL?</label>
          <WizardClearButton onClick={handleClear} clearing={bucket.vazlatLoading} accent={GOLD_ACCENT.eyebrow} testid="landing-sales-clear-btn" />
        </div>
        <textarea
          value={bucket.mit_adunk_el}
          onChange={(e) => patch({ mit_adunk_el: e.target.value })}
          placeholder="Írd le röviden, mit adunk el ezen az oldalon. Ha az Ajánlatban már minden szükséges információ szerepel, hagyd üresen. Ide csak a módosítást vagy a kiegészítést írd: az ajánlat neve, ára, fizetési lehetőségei, elemei, bónuszai, garanciája és fő CTA-ja."
          rows={5} data-testid="landing-sales-mit-adunk-input" style={inputStyle}
        />
      </div>

      {/* 02 — BIZONYÍTÉK */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>02 · BIZONYÍTÉK</label>
        <textarea
          value={bucket.bizonyitek}
          onChange={(e) => patch({ bizonyitek: e.target.value })}
          placeholder="Van visszajelzésed, esettanulmányod vagy konkrét eredményed, amit felhasználhatunk? Másold be pontosan a visszajelzést, és add meg a rendelkezésre álló nevet, eredményt és kontextust. Több visszajelzést külön bekezdésben adj meg. Ha még nincs bizonyítékod, hagyd üresen. A rendszer nem talál ki helyette semmit."
          rows={5} data-testid="landing-sales-bizonyitek-input" style={inputStyle}
        />
      </div>

      {/* 03 — GARANCIA / KOCKÁZATCSÖKKENTÉS */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>03 · GARANCIA / KOCKÁZATCSÖKKENTÉS</label>
        <textarea
          value={bucket.garancia}
          onChange={(e) => patch({ garancia: e.target.value })}
          placeholder="Van garanciád, próbaidőszakod vagy más tényleges kockázatcsökkentő feltételed? Írd le pontosan, hogyan működik. Ha nincs ilyen, hagyd üresen. A rendszer nem talál ki garanciát helyetted."
          rows={3} data-testid="landing-sales-garancia-input" style={inputStyle}
        />
      </div>

      {/* 04 — SÜRGŐSSÉG */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>04 · SÜRGŐSSÉG</label>
        <textarea
          value={bucket.surgosseg}
          onChange={(e) => patch({ surgosseg: e.target.value })}
          placeholder="Van valós határidő, létszámkorlát, árváltozás vagy más tényleges limit? Írd le pontosan, meddig és milyen feltételekkel érvényes. Ha nincs valós sürgősség, hagyd üresen. A rendszer nem alkalmaz kitalált sürgetést."
          rows={3} data-testid="landing-sales-surgosseg-input" style={inputStyle}
        />
      </div>

      {/* 05 — AZ OLDAL CÉLJA */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>05 · AZ OLDAL CÉLJA</label>
        <div style={{ fontSize: 13, color: "#a0a0b8", marginBottom: 12 }}>Mit tegyen az olvasó az oldal végén?</div>
        <div className="flex flex-wrap gap-2" data-testid="landing-sales-oldal-celja">
          {["Vásároljon", "Jelentkezzen", "Foglaljon időpontot", "Csatlakozzon"].map((opt) => (
            <_SalesPill key={opt} value={opt} current={bucket.oldal_celja}
              onClick={() => patch({ oldal_celja: opt })}
              testid={`landing-sales-oldal-celja-${opt.toLowerCase().replace(/\s/g, "-")}`} />
          ))}
        </div>
      </div>

      {/* 06 — HONNAN ÉRKEZIK AZ OLVASÓ? */}
      <div style={{ marginBottom: 8 }}>
        <label style={eyebrowStyle}>06 · HONNAN ÉRKEZIK AZ OLVASÓ?</label>
        <div style={{ fontSize: 13, color: "#a0a0b8", marginBottom: 12 }}>Mennyire ismeri már az olvasó az ajánlatot és téged?</div>
        <div className="flex flex-wrap gap-2" data-testid="landing-sales-forgalom-tipus">
          {[
            "Hideg forgalom - még nem ismer",
            "Webinárról vagy tartalomból érkezik",
            "Meleg lista vagy meglévő közösség",
          ].map((opt) => (
            <_SalesPill key={opt} value={opt} current={bucket.forgalom_tipus}
              onClick={() => patch({ forgalom_tipus: opt })}
              testid={`landing-sales-forgalom-${opt.slice(0, 12).toLowerCase().replace(/\s/g, "-")}`} />
          ))}
        </div>
        <div style={smallNoteStyle}>Ez határozza meg az oldal hangnemét és mélységét.</div>
      </div>

      {/* Generálás CTA — pixelpontosan a Regisztrációs setup MagicGenerateButton-je */}
      <div style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <MagicGenerateButton
          state={loading ? "generating" : (canGenerate ? "selected" : "idle")}
          label={loading ? (bucket.vazlatBtnLabel || "Sales oldal vázlat készül…") : "Sales oldal vázlat generálása"}
          onClick={() => { if (canGenerate && !loading) onGenerate(); }}
          size="lg"
          testid="landing-sales-generate-btn"
        />
        {bucket.vazlatError ? (
          <div style={{ fontSize: 12, color: "#ff6b6b", marginTop: 4 }}>{bucket.vazlatError}</div>
        ) : null}
      </div>
    </div>
  );
}

/* ─── 03 · Sales Mesterműhely ───────────────────────────────────────── */

// A Sales Mesterműhely Sales beszélgetés flow (korábbi „Záró beszélgetés")
// TELJES egészében itt fut: a kártyaválasztás után a setup form belowGrid
// alatt lenyílik (a Webinár / Landing Varázsló mintát követve), a
// generálás ugyanezen a képernyőn zajlik (7 lépéses animált gomb),
// majd a Result screen a helyben renderelődik. A route `/agents/sales`.
// A `/agents/sales-call-closing` route csak `?resume_job=…` esetén él,
// annak kezelését a `SalesCallClosing` wrapper végzi.

const SALES_CLOSING_DRAFT_KEY = "mex_sales_call_closing_draft_v1";
const SALES_CLOSING_SZEREP_OPTIONS = ["Coach", "Mentor", "Tanácsadó", "Szakértő", "Tréner", "Vállalkozó", "Egyéb"];
const SALES_CLOSING_STAGES = [
  "Elemzem az Ajánlatot és az Avatart…",
  "Összeállítom a kérdéseket…",
  "Rendezem a 3 fő problémát…",
  "Felépítem a 3 fő győzelmet…",
  "Kidolgozom a program bemutatását…",
  "Összeállítom az időzítést és az árat…",
  "Finomítom a Sales beszélgetést…",
];
const SALES_CLOSING_STAGE_INTERVAL_MS = 12000;

// ── 10 perces telefon (10 perces) — külön konstansok, külön flow ──
const SALES_INTRO_DRAFT_KEY = "mex_sales_intro_call_draft_v1";
const SALES_INTRO_STAGES = [
  "Elemzem az Ajánlatot és az Avatart…",
  "Testre szabom a nyitó kérdéseket…",
  "Feltérképezem a jelenlegi helyzetet…",
  "Felépítem a Widen the Gap kérdéseket…",
  "Összeállítom az összefoglalást…",
  "Kidolgozom a Fit és No Fit ágakat…",
  "Finomítom a 10 perces telefont…",
];
const SALES_INTRO_STAGE_INTERVAL_MS = 12000;

export function SalesAgent() {
  const navigate = useNavigate();
  const options = [
    { id: "intro",           title: "10 perces telefon", label: "Kiderül, kivel van valódi lehetőség az együttműködésre.",  icon: DeviceMobile },
    { id: "closing",         title: "Sales beszélgetés", label: "Magasan konvertáló értékesítési beszélgetés script",       icon: UsersThree },
    { id: "no-call",         title: "Hívás nélkül",      label: "Tisztán üzenet-alapú, automata ügyfélszerzési folyamat",   icon: Sparkle,      locked: true },
  ];

  // Alapállapot: EGY kártya se legyen kiválasztva (tiszta nyitóállapot).
  // A felhasználó csak akkor lássa az adott agent kérdéseit, amikor
  // tudatosan rákattint az adott kártyára. Frissítés / visszanavigálás
  // után is ez az állapot áll vissza.
  const [selected, setSelected] = useState(null);

  // ── State bucket (draft-perzisztáláshoz és a generálás-flow-hoz) ──
  const emptyBucket = () => ({
    phase: "opening",                 // opening → generating → result
    hivas_kontextus: "", hivas_utan: "",
    kulon_kiemeles: "",
    szerep: "", szerep_egyeb: "",
    // Generálás
    loading: false, loadingStage: 0, errorMsg: "",
    scriptResult: null,
  });
  const [bucket, setBucket] = useState(() => emptyBucket());
  // A localStorage draftot NEM olvassuk vissza az inline form
  // hidratálására — minden új munkamenet üres inputokkal indul.
  // A localStorage-írás (lásd lentebb) megmarad, hogy egy jövőbeli,
  // TUDATOS „Draft folytatása" funkció támaszkodhasson rá.
  const patch = (p) => setBucket((prev) => ({ ...prev, ...p }));
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(SALES_CLOSING_DRAFT_KEY, JSON.stringify({
        hivas_kontextus: bucket.hivas_kontextus,
        hivas_utan: bucket.hivas_utan,
        kulon_kiemeles: bucket.kulon_kiemeles,
        szerep: bucket.szerep,
        szerep_egyeb: bucket.szerep_egyeb,
        scriptResult: bucket.scriptResult,
      }));
    } catch { /* noop */ }
  }, [bucket.hivas_kontextus, bucket.hivas_utan, bucket.kulon_kiemeles,
      bucket.szerep, bucket.szerep_egyeb, bucket.scriptResult]);

  // ── 10 perces telefon — külön state bucket ──────────────────────────
  // Pontosan 3 input: szerep · érkezés · HA ALKALMAS következő lépés.
  const emptyIntroBucket = () => ({
    phase: "opening",
    hivas_kontextus: "",
    fit_kovetkezo_lepes: "",
    szerep: "", szerep_egyeb: "",
    loading: false, loadingStage: 0, errorMsg: "",
    scriptResult: null,
  });
  const [introBucket, setIntroBucket] = useState(() => emptyIntroBucket());
  // Ugyanaz a szabály, mint a closing bucketnél: nincs automatikus
  // hidratálás localStorage-ből — csak tiszta üres form nyílik.
  const introPatch = (p) => setIntroBucket((prev) => ({ ...prev, ...p }));
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(SALES_INTRO_DRAFT_KEY, JSON.stringify({
        hivas_kontextus:     introBucket.hivas_kontextus,
        fit_kovetkezo_lepes: introBucket.fit_kovetkezo_lepes,
        szerep:              introBucket.szerep,
        szerep_egyeb:        introBucket.szerep_egyeb,
        scriptResult:        introBucket.scriptResult,
      }));
    } catch { /* noop */ }
  }, [introBucket.hivas_kontextus, introBucket.fit_kovetkezo_lepes,
      introBucket.szerep, introBucket.szerep_egyeb,
      introBucket.scriptResult]);

  // Név-forrás: Identitásból (activeAlap.voice), nem user input.
  const nev = React.useMemo(() => {
    try {
      const alap = getActiveAlap();
      const raw = alap?.voice?.identity_name || alap?.voice?.name || "";
      return String(raw || "").trim();
    } catch { return ""; }
  }, []);

  // ── Generálás — 7 lépéses animált státusz, async job polling ──
  const stageTimerRef = React.useRef(null);
  const introStageTimerRef = React.useRef(null);
  useEffect(() => () => {
    if (stageTimerRef.current) clearInterval(stageTimerRef.current);
    if (introStageTimerRef.current) clearInterval(introStageTimerRef.current);
  }, []);

  const szerepValid = !!(bucket.szerep && (bucket.szerep !== "Egyéb" || bucket.szerep_egyeb.trim()));
  const canGenerate = !!(bucket.hivas_kontextus.trim() && bucket.hivas_utan.trim() && szerepValid) && !bucket.loading;

  const introSzerepValid = !!(introBucket.szerep && (introBucket.szerep !== "Egyéb" || introBucket.szerep_egyeb.trim()));
  const canGenerateIntro = !!(introBucket.hivas_kontextus.trim() && introBucket.fit_kovetkezo_lepes.trim() && introSzerepValid) && !introBucket.loading;

  const startGeneration = async () => {
    if (!canGenerate) return;
    patch({ errorMsg: "", loading: true, loadingStage: 0, phase: "generating" });
    if (stageTimerRef.current) clearInterval(stageTimerRef.current);
    stageTimerRef.current = setInterval(() => {
      setBucket((prev) => ({
        ...prev,
        loadingStage: prev.loadingStage < SALES_CLOSING_STAGES.length - 1 ? prev.loadingStage + 1 : prev.loadingStage,
      }));
    }, SALES_CLOSING_STAGE_INTERVAL_MS);
    try {
      const alap = getActiveAlap();
      const startRes = await api.post("/sales-call-closing/script/generate-async", {
        voice_id:  alap?.voice_id  || null,
        client_id: alap?.client_id || null,
        offer_id:  alap?.offer_id  || null,
        hivas_kontextus: bucket.hivas_kontextus.trim(),
        hivas_utan:      bucket.hivas_utan.trim(),
        kulon_kiemeles:  bucket.kulon_kiemeles.trim(),
        nev:             nev,
        szerep:          bucket.szerep,
        szerep_egyeb:    bucket.szerep_egyeb.trim(),
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a generálást.");
      // Poll
      let done = false;
      for (let attempt = 0; attempt < 90 && !done; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        const pollRes = await api.get(`/sales-call-closing/script/job/${jobId}`);
        const status = pollRes.data?.status;
        if (status === "done" && pollRes.data?.result) {
          done = true;
          if (stageTimerRef.current) clearInterval(stageTimerRef.current);
          patch({
            scriptResult: pollRes.data.result,
            loading: false, loadingStage: 0,
            phase: "result",
          });
          return;
        }
        if (status === "error") {
          throw new Error(pollRes.data?.error || "A generálás sikertelen.");
        }
      }
      if (!done) throw new Error("Időtúllépés — a generálás túl sokáig tart.");
    } catch (e) {
      if (stageTimerRef.current) clearInterval(stageTimerRef.current);
      const msg = e?.response?.data?.detail || e?.message || "A generálás sikertelen.";
      patch({ loading: false, loadingStage: 0, errorMsg: msg, phase: "opening" });
    }
  };

  const startGenerationIntro = async () => {
    if (!canGenerateIntro) return;
    introPatch({ errorMsg: "", loading: true, loadingStage: 0, phase: "generating" });
    if (introStageTimerRef.current) clearInterval(introStageTimerRef.current);
    introStageTimerRef.current = setInterval(() => {
      setIntroBucket((prev) => ({
        ...prev,
        loadingStage: prev.loadingStage < SALES_INTRO_STAGES.length - 1 ? prev.loadingStage + 1 : prev.loadingStage,
      }));
    }, SALES_INTRO_STAGE_INTERVAL_MS);
    try {
      const alap = getActiveAlap();
      const startRes = await api.post("/sales-intro-call/script/generate-async", {
        voice_id:  alap?.voice_id  || null,
        client_id: alap?.client_id || null,
        offer_id:  alap?.offer_id  || null,
        hivas_kontextus:     introBucket.hivas_kontextus.trim(),
        fit_kovetkezo_lepes: introBucket.fit_kovetkezo_lepes.trim(),
        nev:                 nev,
        szerep:              introBucket.szerep,
        szerep_egyeb:        introBucket.szerep_egyeb.trim(),
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Nem sikerült elindítani a generálást.");
      let done = false;
      for (let attempt = 0; attempt < 90 && !done; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        const pollRes = await api.get(`/sales-intro-call/script/job/${jobId}`);
        const status = pollRes.data?.status;
        if (status === "done" && pollRes.data?.result) {
          done = true;
          if (introStageTimerRef.current) clearInterval(introStageTimerRef.current);
          introPatch({
            scriptResult: pollRes.data.result,
            loading: false, loadingStage: 0,
            phase: "result",
          });
          return;
        }
        if (status === "error") {
          throw new Error(pollRes.data?.error || "A generálás sikertelen.");
        }
      }
      if (!done) throw new Error("Időtúllépés — a generálás túl sokáig tart.");
    } catch (e) {
      if (introStageTimerRef.current) clearInterval(introStageTimerRef.current);
      const msg = e?.response?.data?.detail || e?.message || "A generálás sikertelen.";
      introPatch({ loading: false, loadingStage: 0, errorMsg: msg, phase: "opening" });
    }
  };

  // ── URL-resume: ha `?resume_job=X` query van, betöltjük az adott jobot.
  // Kipróbáljuk mindkét endpoint-namespace-t (closing és intro) — amelyik
  // 200-at ad, azt renderelik. Így egy közös deep-link URL is működik. ──
  useEffect(() => {
    const url = new URL(window.location.href);
    const resumeJob = url.searchParams.get("resume_job");
    if (!resumeJob) return;
    (async () => {
      try {
        const res = await api.get(`/sales-call-closing/script/job/${resumeJob}`);
        const { status, result } = res.data || {};
        if (status === "done" && result && Array.isArray(result.phases)) {
          patch({ scriptResult: result, phase: "result" });
          setSelected("closing");
          return;
        }
      } catch { /* try intro */ }
      try {
        const res = await api.get(`/sales-intro-call/script/job/${resumeJob}`);
        const { status, result } = res.data || {};
        if (status === "done" && result && Array.isArray(result.phases)) {
          introPatch({ scriptResult: result, phase: "result" });
          setSelected("intro");
        }
      } catch { /* noop */ }
    })();
  }, []);

  // ── Result phase render — a kiválasztott kártyához tartozó bucket ──
  if (selected === "intro" && introBucket.phase === "result" && introBucket.scriptResult) {
    return (
      <SalesIntroCallResultScreen
        script={introBucket.scriptResult}
        inputs={{
          hivas_kontextus:     introBucket.hivas_kontextus,
          fit_kovetkezo_lepes: introBucket.fit_kovetkezo_lepes,
          nev,
          szerep:              introBucket.szerep,
          szerep_egyeb:        introBucket.szerep_egyeb,
        }}
        onBack={() => introPatch({ phase: "opening" })}
        onExit={() => navigate("/agents/sales")}
      />
    );
  }
  if (selected === "closing" && bucket.phase === "result" && bucket.scriptResult) {
    return (
      <SalesCallClosingResultScreen
        script={bucket.scriptResult}
        inputs={{
          hivas_kontextus: bucket.hivas_kontextus,
          hivas_utan:      bucket.hivas_utan,
          kulon_kiemeles:  bucket.kulon_kiemeles,
          nev,
          szerep:          bucket.szerep,
          szerep_egyeb:    bucket.szerep_egyeb,
        }}
        onBack={() => patch({ phase: "opening" })}
        onExit={() => navigate("/agents/sales")}
      />
    );
  }

  // ── Opening: 3 kártya + belowGrid inline setup ─────────────────────
  return (
    <AgentOpeningScreen
      eyebrow="ÉRTÉKESÍTÉS · 03 · BESZÉLGETÉS"
      title="Sales Mesterműhely"
      subtitle="Szia! Segítek olyan beszélgetési vázlatokat készíteni, amelyek nyomulás nélkül, tisztán vezetik az érdeklődőt az ügyféllé válás felé."
      options={options}
      columns={3}
      testidPrefix="sales"
      accent={GOLD_ACCENT}
      controlledSelected={selected}
      onSelectOption={(opt) => {
        if (!opt || opt.locked) return;
        setSelected(opt.id);
      }}
      belowGrid={
        selected === "intro" ? (
          <SalesIntroCallSetup
            bucket={introBucket}
            patch={introPatch}
            canGenerate={canGenerateIntro}
            onGenerate={startGenerationIntro}
          />
        ) : selected === "closing" ? (
          <SalesCallClosingSetup
            bucket={bucket}
            patch={patch}
            canGenerate={canGenerate}
            onGenerate={startGeneration}
          />
        ) : null
      }
    />
  );
}

/* ─── 03b · Sales beszélgetés inline setup ─────────────────────────── */

function SalesCallClosingSetup({ bucket, patch, canGenerate, onGenerate }) {
  const handleClear = () => {
    if (bucket.loading) return;
    patch({
      szerep: "",
      szerep_egyeb: "",
      hivas_kontextus: "",
      hivas_utan: "",
      kulon_kiemeles: "",
    });
  };
  const eyebrowStyle = {
    fontSize: 11, color: GOLD_ACCENT.eyebrow, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 14,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };
  const smallInputStyle = { ...inputStyle, padding: "10px 14px" };
  const blockGap = 44; // enyhén nagyobb térköz a kérdésblokkok között (spec: +8-12px)

  const isLoading = bucket.loading;
  const currentStage = isLoading
    ? (SALES_CLOSING_STAGES[bucket.loadingStage] || SALES_CLOSING_STAGES[SALES_CLOSING_STAGES.length - 1])
    : null;
  const buttonLabel = isLoading ? (
    <span style={{ position: "relative", display: "inline-block", minWidth: 240, textAlign: "center" }}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={currentStage}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeInOut" }}
          style={{ display: "inline-block" }}
        >
          {currentStage}
        </motion.span>
      </AnimatePresence>
    </span>
  ) : "Sales beszélgetés generálása";

  return (
    <div
      className="mx-auto"
      style={{ width: "100%", maxWidth: 720, padding: "0 8px", textAlign: "left", marginTop: 72, marginBottom: 36 }}
      data-testid="sales-call-closing-questions"
    >
      {/* 01 · SZEREP */}
      <div style={{ marginBottom: blockGap }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 14 }}>
          <label style={{ ...eyebrowStyle, marginBottom: 0, flex: 1, minWidth: 0 }}>01 · MILYEN SZEREPBEN DOLGOZOL AZ ÜGYFELEIDDEL?</label>
          <WizardClearButton onClick={handleClear} clearing={bucket.loading} accent={GOLD_ACCENT.eyebrow} testid="sales-call-closing-clear-btn" />
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }} data-testid="sales-call-closing-szerep-options">
          {SALES_CLOSING_SZEREP_OPTIONS.map((opt) => {
            const active = bucket.szerep === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => patch({ szerep: opt })}
                data-testid={`sales-call-closing-szerep-${opt.toLowerCase()}`}
                style={{
                  padding: "8px 16px", borderRadius: 999,
                  background: active ? "linear-gradient(180deg, #ffdb5c, #eab308)" : "rgba(255,255,255,0.04)",
                  border: `1px solid ${active ? "#eab308" : "rgba(255,255,255,0.10)"}`,
                  color: active ? "#4a3400" : "#c0c0d0",
                  fontSize: 13, fontWeight: active ? 600 : 500,
                  fontFamily: "inherit", cursor: "pointer",
                  transition: "all 180ms ease",
                }}
              >{opt}</button>
            );
          })}
        </div>
        {bucket.szerep === "Egyéb" && (
          <input
            value={bucket.szerep_egyeb}
            onChange={(e) => patch({ szerep_egyeb: e.target.value })}
            placeholder="Írd be a saját megnevezésedet"
            data-testid="sales-call-closing-szerep-egyeb-input"
            style={{ ...smallInputStyle, marginTop: 12 }}
          />
        )}
      </div>

      {/* 02 · HOGYAN ÉRKEZIK */}
      <div style={{ marginBottom: blockGap }}>
        <label style={eyebrowStyle}>02 · HOGYAN ÉRKEZIK HOZZÁD AZ ÉRDEKLŐDŐ?</label>
        <textarea
          value={bucket.hivas_kontextus}
          onChange={(e) => patch({ hivas_kontextus: e.target.value })}
          placeholder="Például: webinár után foglalt időpontot, ajánlásból érkezett vagy konzultációra jelentkezett."
          rows={4}
          data-testid="sales-call-closing-input-1"
          style={inputStyle}
        />
      </div>

      {/* 03 · MI TÖRTÉNIK A BESZÉLGETÉS UTÁN */}
      <div style={{ marginBottom: blockGap }}>
        <label style={eyebrowStyle}>03 · MI TÖRTÉNIK A BESZÉLGETÉS UTÁN?</label>
        <textarea
          value={bucket.hivas_utan}
          onChange={(e) => patch({ hivas_utan: e.target.value })}
          placeholder="Például: regisztrációs lapot, onboarding kérdőívet és fizetési linket küldök. Írd le a határidőket is."
          rows={4}
          data-testid="sales-call-closing-input-2"
          style={inputStyle}
        />
      </div>

      {/* 04 · KÜLÖN KIEMELÉS (opcionális) */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>04 · VAN BÁRMI, AMIT ENNÉL A SALES BESZÉLGETÉSNÉL KÜLÖN KIEMELNÉL?</label>
        <textarea
          value={bucket.kulon_kiemeles}
          onChange={(e) => patch({ kulon_kiemeles: e.target.value })}
          placeholder="Ha az Ajánlatból nem egyértelmű, ide írhatod a 3 fő győzelmet vagy problémát, amelyekre kihegyeznéd a beszélgetést – vagy bármi mást, amit külön hangsúlyoznál."
          rows={4}
          data-testid="sales-call-closing-input-3"
          style={inputStyle}
        />
      </div>

      {/* Generálógomb — 7 lépéses animált státusz */}
      <div style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <MagicGenerateButton
          state={isLoading ? "generating" : (canGenerate ? "selected" : "idle")}
          label={buttonLabel}
          onClick={() => { if (canGenerate && !isLoading) onGenerate(); }}
          size="lg"
          testid="sales-call-closing-generate-btn"
        />
        {bucket.errorMsg ? (
          <div style={{ fontSize: 12, color: "#ff6b6b", marginTop: 4 }} data-testid="sales-call-closing-error">
            {bucket.errorMsg}
          </div>
        ) : null}
      </div>
    </div>
  );
}


/* ─── 03c · 10 perces telefon (10 perces) inline setup ─────────────── */

function SalesIntroCallSetup({ bucket, patch, canGenerate, onGenerate }) {
  const handleClear = () => {
    if (bucket.loading) return;
    patch({
      szerep: "",
      szerep_egyeb: "",
      hivas_kontextus: "",
      fit_kovetkezo_lepes: "",
    });
  };
  const eyebrowStyle = {
    fontSize: 11, color: GOLD_ACCENT.eyebrow, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 14,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };
  const smallInputStyle = { ...inputStyle, padding: "10px 14px" };
  const blockGap = 44;

  const isLoading = bucket.loading;
  const currentStage = isLoading
    ? (SALES_INTRO_STAGES[bucket.loadingStage] || SALES_INTRO_STAGES[SALES_INTRO_STAGES.length - 1])
    : null;
  const buttonLabel = isLoading ? (
    <span style={{ position: "relative", display: "inline-block", minWidth: 240, textAlign: "center" }}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={currentStage}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeInOut" }}
          style={{ display: "inline-block" }}
        >
          {currentStage}
        </motion.span>
      </AnimatePresence>
    </span>
  ) : "10 perces telefon generálása";

  return (
    <div
      className="mx-auto"
      style={{ width: "100%", maxWidth: 720, padding: "0 8px", textAlign: "left", marginTop: 72, marginBottom: 36 }}
      data-testid="sales-intro-call-questions"
    >
      {/* 01 · SZEREP */}
      <div style={{ marginBottom: blockGap }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 14 }}>
          <label style={{ ...eyebrowStyle, marginBottom: 0, flex: 1, minWidth: 0 }}>01 · MILYEN SZEREPBEN DOLGOZOL AZ ÉRDEKLŐDŐKKEL?</label>
          <WizardClearButton onClick={handleClear} clearing={bucket.loading} accent={GOLD_ACCENT.eyebrow} testid="sales-intro-call-clear-btn" />
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }} data-testid="sales-intro-call-szerep-options">
          {SALES_CLOSING_SZEREP_OPTIONS.map((opt) => {
            const active = bucket.szerep === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => patch({ szerep: opt })}
                data-testid={`sales-intro-call-szerep-${opt.toLowerCase()}`}
                style={{
                  padding: "8px 16px", borderRadius: 999,
                  background: active ? "linear-gradient(180deg, #ffdb5c, #eab308)" : "rgba(255,255,255,0.04)",
                  border: `1px solid ${active ? "#eab308" : "rgba(255,255,255,0.10)"}`,
                  color: active ? "#4a3400" : "#c0c0d0",
                  fontSize: 13, fontWeight: active ? 600 : 500,
                  fontFamily: "inherit", cursor: "pointer",
                  transition: "all 180ms ease",
                }}
              >{opt}</button>
            );
          })}
        </div>
        {bucket.szerep === "Egyéb" && (
          <input
            value={bucket.szerep_egyeb}
            onChange={(e) => patch({ szerep_egyeb: e.target.value })}
            placeholder="Írd be a saját megnevezésedet"
            data-testid="sales-intro-call-szerep-egyeb-input"
            style={{ ...smallInputStyle, marginTop: 12 }}
          />
        )}
      </div>

      {/* 02 · HOGYAN ÉRKEZIK */}
      <div style={{ marginBottom: blockGap }}>
        <label style={eyebrowStyle}>02 · HOGYAN ÉRKEZIK HOZZÁD AZ ÉRDEKLŐDŐ?</label>
        <textarea
          value={bucket.hivas_kontextus}
          onChange={(e) => patch({ hivas_kontextus: e.target.value })}
          placeholder="Például: webinár után foglalt időpontot, ajánlásból érkezett vagy egy ingyenes konzultációra jelentkezett."
          rows={4}
          data-testid="sales-intro-call-input-1"
          style={inputStyle}
        />
      </div>

      {/* 03 · FIT KÖVETKEZŐ LÉPÉS */}
      <div style={{ marginBottom: 28 }}>
        <label style={eyebrowStyle}>03 · HA ILLESZKEDIK HOZZÁD, MI A KÖVETKEZŐ LÉPÉS?</label>
        <textarea
          value={bucket.fit_kovetkezo_lepes}
          onChange={(e) => patch({ fit_kovetkezo_lepes: e.target.value })}
          placeholder="Például: lefoglalunk egy hosszabb Sales beszélgetést, ahol részletesen átbeszéljük a helyzetét és összeállítjuk a tervét."
          rows={4}
          data-testid="sales-intro-call-input-2"
          style={inputStyle}
        />
      </div>

      {/* Generálógomb — 7 lépéses animált státusz */}
      <div style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <MagicGenerateButton
          state={isLoading ? "generating" : (canGenerate ? "selected" : "idle")}
          label={buttonLabel}
          onClick={() => { if (canGenerate && !isLoading) onGenerate(); }}
          size="lg"
          testid="sales-intro-call-generate-btn"
        />
        {bucket.errorMsg ? (
          <div style={{ fontSize: 12, color: "#ff6b6b", marginTop: 4 }} data-testid="sales-intro-call-error">
            {bucket.errorMsg}
          </div>
        ) : null}
      </div>
    </div>
  );
}
